/**
 * <YourEdgeToday />
 *
 * The single highest-priority card on /networker. Removes "where do I
 * begin?" friction by giving the user one clear action based on the
 * time of day.
 *
 * Logic
 *   05:00–11:00  → Identity Activation (morning anchor)
 *   11:00–18:00  → Pre-Call Reset       (workday)
 *   18:00–21:00  → Confidence Reset     (after-meeting recovery)
 *   21:00–05:00  → Identity Activation  (lock-in / night)
 *
 * If the user has already completed an Edge action today, we surface a
 * subtle ✅ "Used today · {streak} day{s}" badge and switch the CTA to
 * "Run again". This creates the small completion loop that drives
 * habit formation.
 */

import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Crown, Phone, RotateCcw, Moon, Check,
} from "lucide-react";
import { hasUsedEdgeToday, getEdgeStreakDays, markEdgeUsedToday } from "./edgeUsageStore";

function pickEdgeOfTheDay() {
  const h = new Date().getHours();
  if (h >= 5 && h < 11) {
    return {
      key: "morning_identity",
      label: "Morning",
      title: "Run your Identity Activation",
      blurb: "Set who you are today. 5 minutes.",
      icon: Crown,
      to: "/networker/audio-library?play=edge_daily_money_identity",
    };
  }
  if (h >= 11 && h < 18) {
    return {
      key: "midday_precall",
      label: "Before your next call",
      title: "Use Pre-Call Reset",
      blurb: "Get clear. Get focused. Go.",
      icon: Phone,
      to: "/networker/resets/pre_call",
    };
  }
  if (h >= 18 && h < 21) {
    return {
      key: "evening_confidence",
      label: "After your last interaction",
      title: "Confidence Reset",
      blurb: "Return to your edge. Lead from there.",
      icon: RotateCcw,
      to: "/networker/resets/confidence",
    };
  }
  return {
    key: "night_lock_in",
    label: "Lock in the day",
    title: "Run your Identity Activation",
    blurb: "Lock in who you are today. 5 minutes.",
    icon: Moon,
    to: "/networker/audio-library?play=edge_daily_money_identity",
  };
}

export default function YourEdgeToday() {
  const navigate = useNavigate();
  const today = useMemo(pickEdgeOfTheDay, []);
  const Icon = today.icon;
  const usedToday = hasUsedEdgeToday();
  const streak = getEdgeStreakDays();

  const handleClick = () => {
    markEdgeUsedToday();
    navigate(today.to);
  };

  return (
    <button
      onClick={handleClick}
      data-testid="your-edge-today-card"
      className="group relative w-full overflow-hidden rounded-2xl text-left transition-transform duration-300 hover:-translate-y-0.5"
      style={{
        background:
          "linear-gradient(135deg, #1A1410 0%, #0E0B08 60%, #050402 100%)",
        border: "1px solid rgba(217,165,90,0.22)",
        boxShadow:
          "0 18px 48px -22px rgba(217,165,90,0.45), inset 0 1px 0 rgba(255,255,255,0.04)",
      }}
    >
      {/* Slow drifting amber glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-90"
        style={{
          background:
            "radial-gradient(circle at 78% 30%, rgba(217,165,90,0.16) 0%, transparent 55%)",
        }}
      />
      {/* Hairline top gradient — feels like a luxury watch */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-0 inset-x-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(217,165,90,0.55), transparent)",
        }}
      />

      <div className="relative px-5 py-6 sm:px-6 sm:py-7 flex items-center gap-5">
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(217,165,90,0.32), rgba(217,165,90,0.10))",
            boxShadow:
              "0 10px 30px -12px rgba(217,165,90,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
          }}
        >
          <Icon className="w-7 h-7 text-amber-300" strokeWidth={1.6} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <p
              className="text-[10px] uppercase tracking-[0.4em] text-amber-500/85"
              data-testid="your-edge-today-label"
            >
              Your Edge Today · {today.label}
            </p>
            {usedToday && (
              <span
                className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.28em] text-emerald-300/85 px-1.5 py-0.5 rounded"
                style={{ background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.22)" }}
                data-testid="your-edge-used-today-badge"
              >
                <Check className="w-2.5 h-2.5" strokeWidth={2.5} /> Used today
                {streak > 1 && <span className="text-emerald-200/70 ml-1">· {streak} days</span>}
              </span>
            )}
          </div>
          <h3
            className="text-white text-xl sm:text-2xl font-semibold leading-tight"
            style={{ letterSpacing: "-0.012em" }}
            data-testid="your-edge-today-title"
          >
            {today.title}
          </h3>
          <p className="text-slate-400/90 text-sm mt-1.5">{today.blurb}</p>
        </div>

        <div
          className="flex items-center gap-2 self-end sm:self-auto px-3 py-2 rounded-full text-[11px] uppercase tracking-[0.18em] text-amber-200 transition-all duration-300 group-hover:translate-x-0.5"
          style={{
            background:
              "linear-gradient(135deg, rgba(217,165,90,0.22), rgba(217,165,90,0.08))",
            border: "1px solid rgba(217,165,90,0.32)",
          }}
        >
          {usedToday ? "Run again" : "Start"}
          <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </button>
  );
}
